<template>
  <section class="HeroAlBronzoCmp">
    <div
      ref="$heroWrapper"
      :class="['wrapper', { animate: intersected && imgLoaded }]"
    >
      <div
        v-if="mdAndUp"
        class="forkful"
        :style="{
          backgroundImage: `url(${$context.staticRoot}project/img/al-bronzo-forkful.png)`,
        }"
      />
      <img
        class="logo"
        :src="`${$context.staticRoot}project/img/logo-al-bronzo.png`"
        :alt="dataAlt"
      />
      <div
        class="plate"
        :style="{
          backgroundImage: `url(${computedSrc})`,
        }"
      />
      <div v-intersect.once="onIntersect" class="intersect" />
    </div>
    <div
      v-if="
        hasSlot($slots['big-title'], $scopedSlots['big-title']) ||
        hasSlot($slots['small-title'], $scopedSlots['small-title']) ||
        hasSlot($slots['text'], $scopedSlots['text'])
      "
      class="container"
    >
      <div class="row justify-content-center">
        <div class="col-12 col-md-11 col-lg-10">
          <div class="content text-center">
            <div class="big-title-wrapper n-heading-1 text-color-bronze">
              <slot name="big-title" />
            </div>
            <div class="small-title-wrapper n-heading-2 text-color-bronze">
              <slot name="small-title" />
            </div>
            <div class="text-wrapper a-paragraph-2">
              <slot name="text" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import imagesLoaded from 'imagesloaded';
import { mdAndUp } from '@/helpers/breakpoints.js';
import Intersect from '@frankhoodbs/lib/src/directives/intersect';
import { hasSlot } from '@/helpers/cms-support';

export default {
  name: 'HeroAlBronzoCmp',
  directives: {
    Intersect,
  },
  props: {
    dataAlt: {
      type: String,
      default: '',
    },
    dataDesktopImageRight: {
      type: String,
      default: null,
    },
    dataMobileImageRight: {
      type: String,
      default: null,
    },
  },
  setup() {
    return { mdAndUp, hasSlot };
  },
  data() {
    return {
      intersected: false,
      imgLoaded: false,
    };
  },
  computed: {
    computedSrc() {
      const usePlaceholders =
        typeof this.dataDesktopImageRight === 'string' &&
        this.dataDesktopImageRight !== ''
          ? false
          : true;

      const desktopBackground = usePlaceholders
        ? `${this.$context.staticRoot}project/img/al-bronzo-plate-desktop.png`
        : this.dataDesktopImageRight;
      const mobileBackground = usePlaceholders
        ? `${this.$context.staticRoot}project/img/al-bronzo-plate-mobile.png`
        : this.dataMobileImageRight
        ? this.dataMobileImageRight
        : this.dataDesktopImageRight;

      return this.mdAndUp ? desktopBackground : mobileBackground;
    },
  },
  mounted() {
    imagesLoaded(this.$refs.$heroWrapper, { background: true }, () => {
      this.imgLoaded = true;
    });
  },
  methods: {
    onIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.intersected = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.HeroAlBronzoCmp {
  background-color: $color-white;

  .wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    height: 640px;
    background: linear-gradient(
      180deg,
      $color-light-red-al-bronzo 0%,
      $color-dark-red-al-bronzo 100%
    );
    margin-top: calc(var(--header-height) * -1);
    padding-top: calc(var(--header-height) + var(--hero-al-bronzo-padding-top));
    isolation: isolate;
    --hero-al-bronzo-padding-top: 204px;

    @include media-breakpoint-up(md) {
      --hero-al-bronzo-padding-top: 181px;
    }

    @include media-breakpoint-up(lg) {
      --hero-al-bronzo-padding-top: 134px;
    }

    @include media-breakpoint-up(xl) {
      --hero-al-bronzo-padding-top: 126px;
    }

    .logo {
      width: 208px;
      height: 153px;
      z-index: 1;

      @include media-breakpoint-up(md) {
        width: 206px;
        height: 151px;
      }

      @include media-breakpoint-up(lg) {
        width: 240px;
        height: 176px;
      }
    }

    .forkful {
      position: absolute;
      top: 121px;
      left: -28px;
      width: 284px;
      height: 367px;
      background-position: right bottom;
      background-size: contain;
      background-repeat: no-repeat;

      @include media-breakpoint-up(lg) {
        top: 0;
        left: 0;
        width: 429px;
        height: 488px;
      }
    }

    .plate {
      position: absolute;
      top: -214px;
      width: 575px;
      height: 575px;
      background-position: center;
      background-size: contain;
      background-repeat: no-repeat;

      @include media-breakpoint-up(md) {
        top: 0;
        right: 0;
        background-position: right top;
        width: 419px;
        height: 483px;
      }

      @include media-breakpoint-up(lg) {
        width: 518px;
        height: 582px;
      }
    }

    .intersect {
      position: absolute;
      top: 50%;
      right: 0;
      left: 0;
      height: 1px;
    }

    & > * {
      opacity: 0;
    }

    .forkful {
      transition: opacity 0.5s ease-out 0.75s;
    }

    .logo {
      transform: scale(0.75);
      transform-origin: center -20px;
      transition: opacity 0.75s ease-out, transform 0.75s ease-out;
    }

    .plate {
      transform: translate(0, -100%);
      transition: opacity 0.5s ease-out 0.75s, transform 0.75s ease-out 0.75s;

      @include media-breakpoint-up(md) {
        transform: translate(100%, -100%);
      }
    }

    &.animate {
      & > * {
        opacity: 1;
      }

      .logo {
        transform: scale(1);
      }

      .plate {
        transform: translate(0, 0);
      }
    }
  }

  .content {
    isolation: isolate;
    min-height: 240px;
    background-color: $color-white;
    border-radius: 8px 8px 0 0;
    padding: 40px 16px;
    margin-top: -196px;

    @include media-breakpoint-up(md) {
      padding: 40px 40px;
      margin-top: -151px;
    }

    @include media-breakpoint-up(lg) {
      margin-top: -120px;
    }

    @include media-breakpoint-up(xl) {
      margin-top: -134px;
    }

    .small-title-wrapper {
      padding-top: 16px;
    }

    .text-wrapper {
      padding-top: 64px;

      @include media-breakpoint-up(md) {
        padding-top: 80px;
      }
    }
  }
}
</style>
